import { Suspense, useState } from 'react';

import { LoaderFunctionArgs } from '@remix-run/node';
import { Await, useNavigate } from '@remix-run/react';
import { Input } from '~/components/ui/input';
import { Button } from '~/components/ui/button';
import { Search } from 'lucide-react';
import CustomErrorBoundary from '~/components/custom-error-boundary';
import Layout from '~/components/layout';
import { Skeleton } from '~/components/ui/skeleton';
import EmblaCarousel from '~/components/ui/embla-carousel/embla-carousel';
import { getHomepageAuctionHouseList } from '~/services/api/auction-houses';
import { getHomepageAuctionsList } from '~/services/api/auctions';
import { validateUser } from '~/services/api/auth';
import type { getCategoryList } from '~/services/api/categories';
import { getHomepageCategoryList } from '~/services/api/categories';
import { getBannerList } from '~/services/api/homepage-banner';
import { getHomepageTrendingProducts } from '~/services/api/products';
import { AuctionStatus } from '~/types';
import { EmblaOptionsType } from 'embla-carousel';
import { isSentryUptimeRootRequest } from '~/lib/uptime-bot';
import { typedjson, useTypedLoaderData } from 'remix-typedjson';

import AuctionHouses from './auction-houses';
import Auctions from './auctions';
import ExploreCategories from './categories';
import TrendingProducts from './products';

export const loader = async ({ request }: LoaderFunctionArgs) => {
  if (isSentryUptimeRootRequest(request)) {
    return typedjson(
      { __uptime: true as const },
      {
        headers: {
          'Cache-Control': 'no-store',
        },
      },
    );
  }

  const [{ user }, banners, categories, auctions, auctionHouses, products] = await Promise.all([
    validateUser(request),
    getBannerList(),
    getHomepageCategoryList(),
    getHomepageAuctionsList({ limit: 12, status: AuctionStatus.APPROVED }),
    getHomepageAuctionHouseList(4),
    getHomepageTrendingProducts({ limit: 5 }),
  ]);

  return typedjson(
    { __uptime: false as const, user, banners, categories, auctions, auctionHouses, products },
    { headers: { 'Cache-Control': 'private, max-age=30, stale-while-revalidate=30' } },
  );
};

const OPTIONS: EmblaOptionsType = { loop: true, duration: 30 };

export function ErrorBoundary() {
  return <CustomErrorBoundary />;
}

function CategoriesSkeleton() {
  return (
    <div className="flex-col gap-4 py-5">
      <div className="border-gray100 border-b pb-4">
        <Skeleton className="h-7 w-48" />
      </div>
      <div className="flex items-center gap-4 overflow-x-hidden pb-4 pt-5 md:gap-10">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-[130px] w-[150px] shrink-0 rounded-lg" />
        ))}
      </div>
    </div>
  );
}

function ProductsSkeleton() {
  return (
    <div className="flex flex-col gap-4 py-5">
      <Skeleton className="h-7 w-48" />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-5">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex flex-col gap-3">
            <Skeleton className="h-60 w-full rounded-lg" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        ))}
      </div>
    </div>
  );
}

function AuctionsSkeleton() {
  return (
    <div className="flex flex-col py-5">
      <Skeleton className="mb-1 h-7 w-48" />
      <Skeleton className="mb-4 h-4 w-32" />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex flex-col gap-3 bg-slate-100 p-3">
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
            <Skeleton className="h-9 w-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

function AuctionHousesSkeleton() {
  return (
    <div className="flex flex-col">
      <Skeleton className="mb-1 h-7 w-56" />
      <Skeleton className="mb-4 h-4 w-40" />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex flex-col items-center gap-3 rounded-lg border py-4 px-4">
            <Skeleton className="h-20 w-20 rounded-lg" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-3 w-32" />
            <Skeleton className="h-9 w-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Index() {
  const data = useTypedLoaderData<typeof loader>();
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');

  if (data.__uptime) return null;

  const { banners, categories, products, auctions, auctionHouses } = data;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const term = searchInput.trim();
    navigate(term ? `/products?search=${encodeURIComponent(term)}` : '/products');
  };

  return (
    <Layout user={data.user as unknown as NonNullable<Awaited<ReturnType<typeof validateUser>>['user']>}>
      <div className="w-full">
        <EmblaCarousel banners={banners.list} options={OPTIONS} />
      </div>

      <form onSubmit={handleSearch} className="mt-6 flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            className="pl-9"
            placeholder="Search products…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <Button type="submit" variant="primary">Search</Button>
      </form>

      <Suspense fallback={<CategoriesSkeleton />}>
        <Await resolve={categories}>
          {(categoriesData) => (
            <ExploreCategories data={categoriesData as unknown as Awaited<ReturnType<typeof getCategoryList>>} />
          )}
        </Await>
      </Suspense>

      <Suspense fallback={<ProductsSkeleton />}>
        <Await resolve={products}>
          {(productsData) => (
            <TrendingProducts data={productsData as unknown as Awaited<ReturnType<typeof getHomepageTrendingProducts>>} />
          )}
        </Await>
      </Suspense>

      <img
        src="/images/best2bid-platform-banner.jpeg"
        alt="Best2Bid: Connecting Culture, Collectors and Commerce"
        className="h-auto w-full"
        loading="lazy"
      />

      <Suspense fallback={<AuctionsSkeleton />}>
        <Await resolve={auctions}>
          {(auctionsData) => (
            <Auctions
              data={auctionsData as unknown as Awaited<ReturnType<typeof getHomepageAuctionsList>>}
              userId={data.user?.id}
            />
          )}
        </Await>
      </Suspense>

      <Suspense fallback={<AuctionHousesSkeleton />}>
        <Await resolve={auctionHouses}>
          {(auctionHousesData) => (
            <AuctionHouses
              data={auctionHousesData as unknown as Awaited<ReturnType<typeof getHomepageAuctionHouseList>>}
            />
          )}
        </Await>
      </Suspense>
    </Layout>
  );
}
